import Button from '@eduzz/houston-ui/Button';
import Showcase from '@eduzz/houston-ui/Showcase';
import { makeStyles } from '@material-ui/core/styles';
import * as React from 'react';
import { useObservable } from 'react-use-observable';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { uiManagerService } from 'v2/services/uiManager';

import { contents } from './contents';

const LOCAL_STORAGE_KEY = 'modal_news_seen';

const useStyles = makeStyles(() => ({
  contentTitle: {
    display: 'block',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
  },
}));

export const ModalNews = React.memo(() => {
  const classes = useStyles();

  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const [loadingTours] = useObservable(() => eduzzAccountsService.getLoadingTours(), []);
  const [currentTour] = useObservable(() => uiManagerService.getCurrentlyTour(), []);
  const [currentStep, setCurrentStep] = React.useState(1);

  const [storagedKeys, setStoragedKeys] = React.useState<string[]>(() => JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || []);
  const [news, setNews] = React.useState([]);
  const [open, setOpen] = React.useState(false);

  const saveNewsToLocalStorage = (news: string[]) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(news));
  };

  const addStepStorageKeyByNumber = (currentStep: number) => {
    const newsStorageKey = news[currentStep - 1].storageKey;
    const updatedStoragedKeys = [...storagedKeys, newsStorageKey];
    setStoragedKeys(updatedStoragedKeys);
    return updatedStoragedKeys;
  };

  const removeStepStorageKeyByNumber = (currentStep: number) => {
    const newsStorageKey = news[currentStep - 2].storageKey;
    const filteredStoragedKeys = storagedKeys.filter((storagedKey) => storagedKey !== newsStorageKey);
    setStoragedKeys(filteredStoragedKeys);
    return filteredStoragedKeys;
  };

  const handleNext = () => {
    setCurrentStep((prev) => prev + 1);
    addStepStorageKeyByNumber(currentStep);
  };

  const handlePrev = () => {
    setCurrentStep((prev) => prev - 1);
    removeStepStorageKeyByNumber(currentStep);
  };

  const handleFinish = () => {
    const lastStepNumber = news.length;
    const updatedStoragedKeys = addStepStorageKeyByNumber(lastStepNumber);
    saveNewsToLocalStorage(updatedStoragedKeys);
    setOpen(false);
  };

  const handleClose = () => {
    saveNewsToLocalStorage(storagedKeys);
    setOpen(false);
  };

  React.useEffect(() => {
    const notSeenContents = contents.filter((content) => !storagedKeys.includes(content.storageKey));
    const accessibleContents = notSeenContents.filter((content) => {
      return content.beta_module ? betaModules?.includes(content.beta_module) : true;
    });
    const shouldShowNews = accessibleContents.length && loadingTours !== null && !loadingTours && !currentTour;
    if (shouldShowNews) {
      setNews(accessibleContents);
      setOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [betaModules, loadingTours, currentTour]);

  const isFirstStep = currentStep === 1;
  const isLastStep = currentStep === news.length;

  return (
    <Showcase open={open} currentStep={currentStep}>
      <Showcase.Content>
        {news?.map((content) => (
          <Showcase.Step key={content.title}>
            {content.image && <Showcase.Image src={content.image} alt={content.title} />}
            <Showcase.Title>{content.title}</Showcase.Title>
            <Showcase.Text>{content.description}</Showcase.Text>
          </Showcase.Step>
        ))}
      </Showcase.Content>
      <Showcase.Footer>
        <div className={classes.footer}>
          <Button variant='text' onClick={handleClose}>
            Ver depois
          </Button>
          <div>
            {!isFirstStep && (
              <Button onClick={handlePrev} variant='text'>
                Anterior
              </Button>
            )}
            {!isLastStep && <Button onClick={handleNext}>Próximo</Button>}
            {isLastStep && <Button onClick={handleFinish}>Entendi</Button>}
          </div>
        </div>
      </Showcase.Footer>
    </Showcase>
  );
});
